import { Loader2 } from "lucide-react"
import type { RefObject } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { usePresentedVideoState } from "@/features/meeting/hooks/usePresentedVideoState"

interface ScreenShareLoadingProps {
  /** Who is sharing. Named only for someone else's share — your own reads
   *  "Loading the shared screen…", not "Loading Sam (You)'s screen…". */
  presenterName?: string
  isOwnShare?: boolean
}

/**
 * Covers the stage while a share has not drawn its first frame.
 *
 * Both stages showed black for that wait: the phone one under "Your screen
 * sharing has stopped" (BUG-000750), the desktop one as a bare black box
 * (BUG-000740). It lasts as long as the presenter's first keyframe takes to
 * reach this viewer through the SFU, which testers read as the share failing.
 */
export function ScreenShareLoading({ presenterName, isOwnShare = false }: ScreenShareLoadingProps) {
  const t = useTranslation()
  return (
    <div
      role="status"
      aria-live="polite"
      className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-gray-900 px-4 text-center"
    >
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <span className="text-xs sm:text-sm font-medium text-white/80">
        {!isOwnShare && presenterName
          ? t("screenShare.loadingScreenOf", { name: presenterName })
          : t("screenShare.loadingScreen")}
      </span>
    </div>
  )
}

/**
 * ScreenShareLoading for a stage that does not track the video itself (the
 * desktop one). Render it after the `<video>`, inside the stage, so it mounts
 * with the element it watches.
 */
export function ScreenShareLoadingOverlay({
  videoRef,
  ...props
}: ScreenShareLoadingProps & { videoRef: RefObject<HTMLVideoElement | null> }) {
  const state = usePresentedVideoState(videoRef)
  return state === "loading" ? <ScreenShareLoading {...props} /> : null
}
